<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProjectStatusReportsAPI from 'dashboard/api/projectStatusReports';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['count']);

const { t } = useI18n();

const RAG_OPTIONS = ['unknown', 'green', 'amber', 'red'];

const RAG_DOT = {
  green: 'bg-n-teal-9',
  amber: 'bg-n-amber-9',
  red: 'bg-n-ruby-9',
  unknown: 'bg-n-slate-7',
};

const emptyForm = () => ({
  reporting_period_start: '',
  reporting_period_end: '',
  overall_health: 'green',
  schedule_health: 'green',
  cost_health: 'green',
  scope_health: 'green',
  summary: '',
  accomplishments: '',
  upcoming: '',
  blockers: '',
});

const reports = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const isSaving = ref(false);
const form = ref(emptyForm());

const loadReports = async () => {
  isLoading.value = true;
  try {
    const { data } = await ProjectStatusReportsAPI.get(props.projectId);
    reports.value = data || [];
  } catch (error) {
    reports.value = [];
  } finally {
    isLoading.value = false;
    emit('count', reports.value.length);
  }
};

const openForm = () => {
  form.value = emptyForm();
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
};

const saveReport = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    await ProjectStatusReportsAPI.create(props.projectId, form.value);
    showForm.value = false;
    await loadReports();
  } catch (error) {
    useAlert(t('PROJECTS.STATUS_REPORTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.projectId, loadReports);
onMounted(loadReports);
</script>

<template>
  <component
    :is="embedded ? 'div' : 'section'"
    :class="
      embedded
        ? 'flex flex-col gap-3'
        : 'flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-2'
    "
  >
    <header
      v-if="!embedded"
      class="flex items-center justify-between gap-2 px-4 h-11 border-b border-n-weak"
    >
      <span
        class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
      >
        <span class="i-lucide-clipboard-list size-4 text-n-slate-10" />
        {{ t('PROJECTS.STATUS_REPORTS.TITLE') }}
      </span>
      <Button
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('PROJECTS.STATUS_REPORTS.ADD')"
        @click="openForm"
      />
    </header>

    <Button
      v-if="embedded && !showForm"
      variant="faded"
      color="slate"
      size="sm"
      icon="i-lucide-plus"
      class="self-start"
      :label="t('PROJECTS.STATUS_REPORTS.ADD')"
      @click="openForm"
    />

    <div :class="embedded ? 'flex flex-col gap-3' : 'flex flex-col gap-3 p-4'">
      <!-- New report form -->
      <div
        v-if="showForm"
        class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak bg-n-background"
      >
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.STATUS_REPORTS.FIELDS.PERIOD_START') }}
            <input
              v-model="form.reporting_period_start"
              type="date"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.STATUS_REPORTS.FIELDS.PERIOD_END') }}
            <input
              v-model="form.reporting_period_end"
              type="date"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            />
          </label>
        </div>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <label
            v-for="field in [
              'overall_health',
              'schedule_health',
              'cost_health',
              'scope_health',
            ]"
            :key="field"
            class="flex flex-col gap-1 text-xs text-n-slate-10"
          >
            {{ t(`PROJECTS.STATUS_REPORTS.FIELDS.${field.toUpperCase()}`) }}
            <select
              v-model="form[field]"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="rag in RAG_OPTIONS" :key="rag" :value="rag">
                {{ t(`PROJECTS.HEALTH.${rag.toUpperCase()}`) }}
              </option>
            </select>
          </label>
        </div>
        <textarea
          v-model="form.summary"
          rows="2"
          :placeholder="t('PROJECTS.STATUS_REPORTS.FIELDS.SUMMARY')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <textarea
          v-model="form.accomplishments"
          rows="2"
          :placeholder="t('PROJECTS.STATUS_REPORTS.FIELDS.ACCOMPLISHMENTS')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <textarea
          v-model="form.upcoming"
          rows="2"
          :placeholder="t('PROJECTS.STATUS_REPORTS.FIELDS.UPCOMING')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <textarea
          v-model="form.blockers"
          rows="2"
          :placeholder="t('PROJECTS.STATUS_REPORTS.FIELDS.BLOCKERS')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <div class="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('PROJECTS.STATUS_REPORTS.CANCEL')"
            @click="cancelForm"
          />
          <Button
            size="sm"
            color="blue"
            :label="t('PROJECTS.STATUS_REPORTS.SAVE')"
            :is-loading="isSaving"
            @click="saveReport"
          />
        </div>
      </div>

      <!-- Report list -->
      <ProjectEmptyState
        v-if="!reports.length && !isLoading"
        compact
        icon="i-lucide-clipboard-list"
        :title="t('PROJECTS.STATUS_REPORTS.EMPTY')"
      />
      <ul v-else class="flex flex-col divide-y divide-n-weak/60">
        <li
          v-for="report in reports"
          :key="report.id"
          class="flex flex-col gap-1 py-2.5"
        >
          <div class="flex items-center gap-2">
            <span
              class="rounded-full size-2.5 shrink-0"
              :class="RAG_DOT[report.overall_health] || RAG_DOT.unknown"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ report.reporting_period_start || '—' }} →
              {{ report.reporting_period_end || '—' }}
            </span>
          </div>
          <p
            v-if="report.summary"
            class="text-sm whitespace-pre-line text-n-slate-11"
          >
            {{ report.summary }}
          </p>
        </li>
      </ul>
    </div>
  </component>
</template>
